import { Link } from "wouter";
import { Facebook, Instagram, Youtube, Twitter, MapPin, Phone, AlertCircle } from "lucide-react";
import type { ReactNode } from "react";
import { CONTACT_INFO } from "@/config/contact";

const Discover_LINKS: Record<string, string> = {
  "Venues": "/venues",
  "Vendors": "/vendors",
  "Wedding Planners": "/wedding-planners",
  "Photographers": "/photographers",
  "Weddings": "/weddings",
};

const TOOLS_LINKS: Record<string, string> = {
  "Guest List Manager": "/guest-list",
  "Budget Calculator": "/budget-calculator",
  "Hashtag Generator": "/hashtag-generator",
};

const SERVICE_LINKS: Record<string, string> = {
  "Photography":  "/vendors?category=PHOTOGRAPHER",
  "Makeup":       "/vendors?category=MAKEUP+ARTIST",
  "Catering":     "/vendors?category=CATERER",
  "Decorator":    "/vendors?category=DECOR",
  "Entertainment":"/vendors?category=MUSIC+%26+DJ",
};

const Company_LINKS: Record<string, string> = {
  "About Us": "/about",
  "Careers": "/careers",
  "News & Press": "/news",
  "Contact Us": "/contact-us",
  "Privacy Policy": "/privacy-policy",
  "Terms of Service": "/terms-of-service",
  "Cookie Policy": "/cookie-policy",
};

const CITY_LINKS: Record<string, string> = {
  "Mumbai":    "/venues?city=Mumbai",
  "Delhi":     "/venues?city=Delhi",
  "Jaipur":    "/venues?city=Jaipur",
  "Goa":       "/venues?city=Goa",
  "Udaipur":   "/venues?city=Udaipur",
  "Bangalore": "/venues?city=Bangalore",
  "Hyderabad": "/venues?city=Hyderabad",
  "Kolkata":   "/venues?city=Kolkata",
};

const SOCIAL = [
  {
    icon: <Instagram className="w-4 h-4" />,
    label: "Instagram",
    href: "https://www.instagram.com/bookmysquadd",
  },
  {
    icon: <Facebook className="w-4 h-4" />,
    label: "Facebook",
    href: "https://www.facebook.com/bookmysquadd",
  },
{
  icon: (
    <svg
      viewBox="0 0 24 24"
      className="w-4 h-4"
      fill="currentColor"
    >
      <path d="M18.244 2H21l-6.56 7.5L22 22h-6.828l-5.35-6.99L3.72 22H1l7.02-8.02L2 2h7l4.84 6.37L18.244 2Zm-1.197 18h1.89L7.91 3.895H5.882L17.047 20Z" />
    </svg>
  ),
  label: "X",
  href: "https://x.com/bookmysquadd",
},
 {
    icon: <Youtube className="w-4 h-4" />,
    label: "YouTube",
    href: "https://www.youtube.com/@bookmysquadd",
  },
];

function FooterLink({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}) 

{
    const isExternal = href.startsWith("http");
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="font-manrope text-sm text-gray-800/55 hover:text-primary/90 transition-colors duration-300">
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className="font-manrope text-sm text-gray-800/55 hover:text-primary/90 transition-colors duration-300">
      {children}
    </Link>
  );
}

export function Footer() {
  return (
    <footer className="relative bg-black/95 text-gray-800 overflow-hidden backdrop-blur-xl border-t border-white/5">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(212,175,55,0.08),transparent_30%),radial-gradient(circle_at_bottom,rgba(255,190,70,0.05),transparent_20%)] pointer-events-none" />
 <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden -z-10">
  <span
    className="normal-case font-cinzel font-black tracking-wider"
    style={{
      fontSize: "18vw",
      opacity: 0.03,
      color: "currentColor",
      filter: "blur(1px)",
    }}
  >
    BMS
  </span>
</div>
     
   {/* Top glow line */}
      <div className="gold-line" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-20 blur-3xl bg-primary/10 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-20 pb-10">

        {/* Logo + Tagline */}
        <div className="text-center mb-14 md:mb-20">
          <div className="normal-case font-cinzel text-[10px] tracking-[0.5em] text-primary/60  mb-4">✦ Est. 2024 ✦</div>
          <Link href="/" className="flex flex-col items-center gap-3 mb-4 cursor-pointer"></Link>
          <p className="font-manrope text-gray-800/45 text-sm tracking-widest ">Premium Event Planning</p>
          <div className="gold-line w-24 mx-auto mt-8" />
        </div>

        {/* Main links + offices grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-10 mb-14 md:mb-20">

          {/* Discover */}
          <div>
<h4 className="!text-[20px] mb-5 leading-tight whitespace-nowrap" style={{
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "20px",
      fontStyle: "italic",
      fontWeight: 500,
      letterSpacing: "0.02em",
      textTransform: "none",
      lineHeight: 1.1
    }}>
  Discover
</h4>
  <ul className="space-y-3">
              {Object.entries(Discover_LINKS).map(([label, href]) => (
                <li key={label}>
                  <FooterLink href={href}>{label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Planning Tools */}
         <div>
  <h4 className="!text-[20px] mb-5 leading-tight whitespace-nowrap text-black dark:text-primary/80" style={{
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "20px",
      fontStyle: "italic",
      fontWeight: 500,
      letterSpacing: "0.02em",
      textTransform: "none",
      lineHeight: 1.1
    }}>
    Planning Tools
  </h4>
  <ul className="space-y-3">
              {Object.entries(TOOLS_LINKS).map(([label, href]) => (
                <li key={label}>
                  <FooterLink href={href}>{label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="!text-[20px] mb-5 leading-tight whitespace-nowrap text-black dark:text-primary/80" style={{
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "20px",
      fontStyle: "italic",
      fontWeight: 500,
      letterSpacing: "0.02em",
      textTransform: "none",
      lineHeight: 1.1
    }}>
  Services
</h4>
            <ul className="space-y-3">
              {Object.entries(SERVICE_LINKS).map(([label, href]) => (
                <li key={label}>
                  <FooterLink href={href}>{label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="!text-[20px] mb-5 leading-tight whitespace-nowrap text-black dark:text-primary/80" style={{
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "20px",
      fontStyle: "italic",
      fontWeight: 500,
      letterSpacing: "0.02em",
      textTransform: "none",
      lineHeight: 1.1
    }}>
  Company
</h4>
            <ul className="space-y-3">
              {Object.entries(Company_LINKS).map(([label, href]) => (
                <li key={label}>
                  <FooterLink href={href}>{label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Cities */}
          <div>
            <h4 className="!text-[20px] mb-5 leading-tight whitespace-nowrap text-black dark:text-primary/80" style={{
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "20px",
      fontStyle: "italic",
      fontWeight: 500,
      letterSpacing: "0.02em",
      textTransform: "none",
      lineHeight: 1.1
    }}>
  Cities
</h4>
            <ul className="space-y-3">
              {Object.entries(CITY_LINKS).map(([label, href]) => (
                <li key={label}>
                  <FooterLink href={href}>{label}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

{/* Contact & Support */}
<div className="col-span-2 md:col-span-3 lg:col-span-1">

<h4 className="!text-[20px] mb-5 leading-tight whitespace-nowrap text-black dark:text-primary/80" style={{
      fontFamily: "'Cormorant Garamond', Georgia, serif",
      fontSize: "20px",
      fontStyle: "italic",
      fontWeight: 500,
      letterSpacing: "0.02em",
      textTransform: "none",
      lineHeight: 1.1
    }}>
  Customer Support
</h4>

  <div className="space-y-4">
    <a
      href={`mailto:${CONTACT_INFO.supportEmail}`}
      className="block font-manrope text-sm text-gray-800/65 hover:text-primary transition-colors"
    >
      <span className="block font-semibold text-gray-800">
        {CONTACT_INFO.supportEmail}
      </span>
      <span className="text-gray-800/45 text-xs">General support</span>
    </a>

    <a
      href={CONTACT_INFO.phoneHref}
      className="block font-manrope text-sm text-gray-800/65 hover:text-primary transition-colors"
    >
      <span className="block font-semibold text-gray-800">
        {CONTACT_INFO.phone}
      </span>
      <span className="text-gray-800/45 text-xs">Customer care</span>
    </a>

    <a
      href={`mailto:${CONTACT_INFO.salesEmail}`}
      className="block font-manrope text-sm text-gray-800/65 hover:text-primary transition-colors"
    >
      <span className="block font-semibold text-gray-800">
        {CONTACT_INFO.salesEmail}
      </span>
      <span className="text-gray-800/45 text-xs">Business enquiries</span>
    </a>

    <div className="text-gray-800/50 text-sm leading-relaxed space-y-1">
      <p>Mon–Sat | 10 AM – 7 PM</p>
      <p>Usually responds within 24 hours</p>
    </div>

     </div>
</div>

</div>

{/* Social Icons */}
<div className="flex justify-center gap-6 mb-10">
  {SOCIAL.map((s) => (
    <a
      key={s.label}
      href={s.href}
      target="_blank"
      rel="noopener noreferrer"
      className="w-10 h-10 border border-primary/40 hover:border-primary flex items-center justify-center text-gray-800/60 hover:text-primary transition-all"
    >
      {s.icon}
    </a>
  ))}
</div>

{/* Full Width Disclaimer */}
<div
  className="w-full max-w-7xl mx-auto mb-10 rounded-sm border border-primary/40 px-10 py-8"
  style={{
    background:
      "linear-gradient(135deg, rgba(212,175,55,.10), rgba(212,175,55,.04))",
    boxShadow:
      "0 0 30px rgba(212,175,55,.15), inset 0 0 30px rgba(212,175,55,.05)",
  }}
>
  <div className="flex items-start gap-6">
    <div className="w-14 h-14 rounded-full border border-primary/60 flex items-center justify-center shrink-0">
      <AlertCircle className="w-7 h-7 text-primary" />
    </div>

    <div>
      <h3 className="normal-case font-manrope  tracking-normal text-primary text-base mb-5">
        IMPORTANT DISCLAIMER
      </h3>

      <p className="text-gray-800/70 leading-8">
        Book My Squadd is a marketplace platform connecting event planners,
        couples and vendors. Vendors and venues operate independently.
        Book My Squadd does not guarantee the quality, legality, safety,
        availability or pricing of listed services. Users should verify all
        information before making bookings or payments.
      </p>
    </div>
  </div>
</div>

{/* Policy Links */}
<div className="flex flex-wrap justify-center gap-8 mb-8">
  <Link href="/privacy-policy">Privacy Policy</Link>
  <Link href="/terms-of-service">Terms of Service</Link>
  <Link href="/cookie-policy">Cookie Policy</Link>
  </div>

<div className="gold-line mb-6" />

<p className="text-center text-gray-800/40 text-xs tracking-widest">
  © 2026 Book My Squadd. All Rights Reserved.
</p>

    </div>
    </footer>
  );
}
